import { useState } from "react";
import { Link } from "react-router-dom";
import type { GroupView } from "@starsach/shared-types";
import { useAccess, useDeleteGroup, useGroups, useRoles } from "../../api/hooks";
import { useStepUp } from "../../auth/StepUpProvider";
import { useEnvironmentName } from "../../components/Environment";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, btnPrimary, btnSecondary } from "../../components/ui";
import { RolePills } from "./parts";

/** Groups (ADR-0017 section 2): a named set of roles, its members, and the environments the roles apply on. */
export function GroupsSection({ environmentId }: { environmentId: string }) {
  const q = useGroups();
  const roles = useRoles();
  const access = useAccess();
  const remove = useDeleteGroup();
  const { ensureFresh } = useStepUp();
  const envName = useEnvironmentName();
  const [deleting, setDeleting] = useState<GroupView | null>(null);
  const canCreate = access.can(environmentId, "roles:create");
  const canEdit = access.can(environmentId, "roles:edit");
  const canDelete = access.can(environmentId, "roles:delete");

  if (q.isPending) return <LoadingState label="Loading groups" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <Card title={<>Groups<Hint id="access.groups" label="what a group is" /></>}>
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
          A group gives several people the same roles on the same environments at once. A member holds the group's roles
          there in addition to any granted to them directly.
        </p>
        <MutationError of={remove} className="mb-3" />
        <DataTable
          caption="Groups in your organization"
          rows={q.data.groups}
          rowKey={(g) => g.groupId}
          sortBy={(g) => g.name}
          empty={<EmptyState title="No groups yet" hint="A group is worth making when several people should hold the same roles on the same environments." />}
          columns={[
            { key: "name", header: "Group", render: (g) => <span className="font-medium">{g.name}</span> },
            { key: "roles", header: "Roles", render: (g) => <RolePills roleIds={g.roleIds} roles={roles.data?.roles} /> },
            { key: "members", header: "Members", render: (g) => (g.members.length === 0 ? <span className="text-sm text-neutral-500">none</span> : <span className="text-sm">{g.members.map((m) => m.email ?? m.userId).join(", ")}</span>) },
            { key: "envs", header: "Environments", render: (g) => (g.environmentIds.length === 0 ? <span className="text-sm text-neutral-500">none</span> : <span className="text-sm">{g.environmentIds.map(envName).join(", ")}</span>) },
            { key: "updated", header: "Changed", render: (g) => <RelativeTime iso={g.updatedAt} /> },
            {
              key: "actions",
              header: "",
              render: (g) => (
                <span className="flex gap-2">
                  {canEdit && (
                    <Link to={`/access/${environmentId}/groups/${encodeURIComponent(g.groupId)}`} className="text-sm underline" aria-label={`Edit ${g.name}`}>
                      Edit
                    </Link>
                  )}
                  {canDelete && (
                    <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => setDeleting(g)}>Delete</button>
                  )}
                </span>
              ),
            },
          ]}
        />
        <div className="mt-4">
          {canCreate ? (
            <Link to={`/access/${environmentId}/groups/new`} className={btnPrimary}>
              New group
            </Link>
          ) : (
            <NotHeld permission="roles:create" />
          )}
        </div>
      </Card>
      {deleting && (
        <Dialog title={`Delete ${deleting.name}?`} onClose={() => setDeleting(null)}>
          <p className="text-sm">
            {deleting.members.length === 0 ? "The group has no members." : `${deleting.members.length} member${deleting.members.length === 1 ? "" : "s"} lose${deleting.members.length === 1 ? "s" : ""} the group's roles at once; roles granted to them directly stay.`}
          </p>
          <MutationError of={remove} className="mt-3" />
          <div className="mt-4 flex gap-2">
            <button type="button" className={btnPrimary} disabled={remove.isPending} onClick={() => void ensureFresh().then(() => remove.mutate({ groupId: deleting.groupId }, { onSuccess: () => setDeleting(null) }), () => undefined)}>
              {remove.isPending ? "Deleting…" : "Delete"}
            </button>
            <button type="button" className={btnSecondary} onClick={() => setDeleting(null)}>Cancel</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
